import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { PromoCampaign } from '@/app/types/promo-code-type';
import { getPromoValidation } from '@/lib/platform/actions/get-promo-validation';
const logger = console;

export interface PromoCodeData {
  isLoading: boolean;
  status: PromoStatus | null;
  promoCampaign: PromoCampaign | null;
  code: string | null;
}

export interface PromoCodeState extends PromoCodeData {
  applyCode: (code: string) => void;
}

export enum PromoStatus {
  valid = 'valid',
  invalid = 'invalid',
  expired = 'expired',
  empty = 'empty',
  error = 'error',
}

const usePromoCodeStore = create<PromoCodeState>()(
  devtools(
    subscribeWithSelector((set) => ({
      isLoading: false,
      code: null,
      status: null,
      promoCampaign: null,
      applyCode: async (code: string) => {
        const trimmedCode = code.trim();
        if (trimmedCode == '') {
          set(
            {
              status: PromoStatus.empty,
              code: trimmedCode,
              promoCampaign: null,
            },
            undefined,
            'usePromoCodeStore/applyCodeStatus'
          );
          return;
        }

        set(
          { isLoading: true },
          undefined,
          'usePromoCodeStore/applyCodeIsLoading'
        );
        try {
          const response = await getPromoValidation(trimmedCode);
          if (response.error) {
            if (response.error == 'ExpiredPromoError') {
              set(
                { status: PromoStatus.expired, promoCampaign: null },
                undefined,
                'usePromoCodeStore/applyCodeStatus'
              );
            }
            if (response.error == 'InvalidPromoError') {
              set(
                { status: PromoStatus.invalid, promoCampaign: null },
                undefined,
                'usePromoCodeStore/applyCodeStatus'
              );
            }
            return;
          }

          set(
            { status: PromoStatus.valid, promoCampaign: response.data },
            undefined,
            'usePromoCodeStore/applyCode'
          );
        } catch (err: unknown) {
          set(
            { status: PromoStatus.error, promoCampaign: null },
            undefined,
            'usePromoCodeStore/applyCodeStatus'
          );
          logger.error(err);
        } finally {
          set(
            { isLoading: false, code: trimmedCode },
            undefined,
            'usePromoCodeStore/applyCodeComplete'
          );
        }
      },
    }))
  )
);

export const validPromoCodeSelector = (state: PromoCodeState) =>
  state.promoCampaign?.promoCode;

export default usePromoCodeStore;
